import { supabase } from '@/lib/supabase';
import type { Session } from '@supabase/supabase-js';

// =============================================================
// 타입 정의 ------------------------------------------------------
export interface InstagramProfile {
  biography: string;
  followersCount: number;
  followsCount: number;
  fullName: string;
  postsCount: number;
  profilePicUrl: string;
  profilePicUrlHD: string;
  username: string;
  isBusinessAccount: boolean;
  isVerified: boolean;
  originalProfilePicUrl?: string;
  originalProfilePicUrlHD?: string;
  latestPosts: Array<{
    id: string;
    displayUrl: string;
    originalDisplayUrl?: string;
    videoUrl?: string;
    type: 'image' | 'video';
    likesCount: number;
    commentsCount: number;
    caption: string;
  }>;
  source?: string;
}

interface FetchResponse {
  processed_result: InstagramProfile;
  original_data?: any[];
  latestPostsImgbbUrls?: Array<{
    postId: string;
    imgbbUrl: string | null;
  }>;
  source?: string;
  error?: string;
  errorCode?: string;
}

// 인스타그램 프로필 패치 옵션 인터페이스
interface FetchInstagramProfileOptions {
  fetchTimeout?: number; // 타임아웃 (ms)
  onProgress?: (progress: number) => void; // 진행도 콜백
  retryCount?: number; // 재시도 횟수
}

// ---------- 유효성 검사 ----------
const USERNAME_REGEX = /^[a-zA-Z0-9_.]{1,30}$/;
export function isValidInstagramUsername(username: string): boolean {
  return USERNAME_REGEX.test(username);
}

// ---------- 캐시 & 락 ----------
const ongoingRequests = new Map<string, Promise<FetchResponse>>();
const profileCache = new Map<string, { data: FetchResponse; timestamp: number }>();
const dbLookupPromises = new Map<string, Promise<{ data: any; error: any }>>();
const failureCache = new Map<string, number>();
const CACHE_DURATION = 30 * 60 * 1000; // 30 분
const FAILURE_COOLDOWN = 60 * 1000; // 1분

// ---------- 세션 메모이제이션 ----------
let cachedSession: Session | null = null;
let sessionLastChecked = 0;
const SESSION_MAX_AGE = 5 * 60 * 1000; // 5분

async function getSession(): Promise<Session | null> {
  const now = Date.now();
  // 캐시된 세션이 있고 유효기간 내라면 그대로 사용
  if (cachedSession && now - sessionLastChecked < SESSION_MAX_AGE) {
    return cachedSession;
  }
  
  // 세션 갱신
  const { data } = await supabase.auth.getSession();
  cachedSession = data.session ?? null;
  sessionLastChecked = now;
  return cachedSession;
}

// ---------- 재시도 fetch ----------
async function fetchWithRetry(
  url: string,
  options: RequestInit,
  retries = 3,
  backoff = 300,
  onProgress?: (progress: number) => void
): Promise<Response> {
  try {
    // 진행 상태 업데이트 (1번째 시도 = 20%, 2번째 = 40%, 3번째 = 60%)
    const progressValue = 20 + ((3 - retries) * 20);
    onProgress?.(progressValue);
    
    const res = await fetch(url, options);
    if (!res.ok && (res.status === 429 || res.status >= 500) && retries > 0) {
      const retryAfter = res.headers.get('Retry-After');
      const wait = retryAfter ? +retryAfter * 1000 : backoff;
      await new Promise(r => setTimeout(r, wait));
      return fetchWithRetry(url, options, retries - 1, backoff * 2, onProgress);
    }
    return res;
  } catch (err: any) {
    if (err.name === 'AbortError') throw err;
    if (retries === 0) throw err;
    await new Promise(r => setTimeout(r, backoff));
    return fetchWithRetry(url, options, retries - 1, backoff * 2, onProgress);
  }
}

// ---------- Toast (콘솔) ----------
function toast(id: string, msg: string) {
  console.log(`[Toast ${id}] ${msg}`);
}

// ---------- 이미지 URL 변환 유틸리티 ----------
function transformImgBBUrls(profile: InstagramProfile): InstagramProfile {
  // ImgBB URLs can sometimes have issues - ensure they're properly formatted
  if (profile.profilePicUrl && profile.profilePicUrl.includes('i.ibb.co')) {
    profile.profilePicUrl = ensureValidImgBBUrl(profile.profilePicUrl);
  }
  
  if (profile.profilePicUrlHD && profile.profilePicUrlHD.includes('i.ibb.co')) {
    profile.profilePicUrlHD = ensureValidImgBBUrl(profile.profilePicUrlHD);
  }
  
  // Also process any post images
  if (profile.latestPosts) {
    profile.latestPosts = profile.latestPosts.map(post => {
      if (post.displayUrl && post.displayUrl.includes('i.ibb.co')) {
        return {
          ...post,
          displayUrl: ensureValidImgBBUrl(post.displayUrl)
        };
      }
      return post;
    });
  }
  
  return profile;
}

// ImgBB URL 검증 및 수정
function ensureValidImgBBUrl(url: string): string {
  try {
    // If URL is missing https:// prefix, add it
    if (url.startsWith('//i.ibb.co')) {
      url = 'https:' + url;
    } else if (url.startsWith('i.ibb.co')) {
      url = 'https://' + url;
    }
    
    // Verify this is a proper URL
    new URL(url);
    return url;
  } catch (e) {
    console.warn('Invalid ImgBB URL, using fallback:', url);
    return '/default-profile.png'; // Fallback to default image
  }
}

// =============================================================
// 메인 함수 ------------------------------------------------------
export async function fetchInstagramProfile(
  username: string,
  options: FetchInstagramProfileOptions = {}
): Promise<FetchResponse> {
  // Convert username to lowercase to avoid duplicate API calls
  const rawUsername = username;
  const usernameLower = rawUsername.trim().toLowerCase();
  if (!isValidInstagramUsername(usernameLower)) throw new Error('유효하지 않은 인스타그램 아이디');

  // 최근 실패한 요청인지 확인
  const lastFail = failureCache.get(usernameLower);
  if (lastFail && Date.now() - lastFail < FAILURE_COOLDOWN) {
    throw new Error('잠시 후 다시 시도해주세요');
  }

  // 진행 중인 요청 확인 (동일 사용자명에 대한 중복 요청 방지)
  if (ongoingRequests.has(usernameLower)) {
    return ongoingRequests.get(usernameLower)!;
  }

  // 새 요청 생성 및 락 설정
  const promise = (async (): Promise<FetchResponse> => {
    const now = Date.now();

    // 1) 메모리 캐시
    const mem = profileCache.get(usernameLower);
    if (mem && now - mem.timestamp < CACHE_DURATION) {
      console.log(`[Memory Cache] Using cached profile for ${usernameLower}`);
      return mem.data;
    }

    // 2) DB 캐시
    const session = await getSession();
    if (session?.user) {
      let dbPromise = dbLookupPromises.get(usernameLower);
      if (!dbPromise) {
        dbPromise = (async () => {
          console.log(`[DB Lookup] Checking Supabase for ${usernameLower}`);
          return await supabase
            .from('public_instagram_accounts')
            .select('profile_data, updated_at, supabase_profile_pic_url, supabase_profile_hd_pic_url')
            .eq('username', usernameLower)
            .eq('user_id', session.user.id)
            .is('deleted_at', null)
            .maybeSingle();
        })();
        dbLookupPromises.set(usernameLower, dbPromise);
        dbPromise.finally(() => dbLookupPromises.delete(usernameLower));
      }
      
      const { data: saved, error } = await dbPromise;
      
      if (!error && saved?.profile_data) {
        const age = (Date.now() - new Date(saved.updated_at).getTime()) / 60000;
        if (age < 10080) {  // 30분 이내 캐시는 유효
          console.log(`[DB Cache Hit] Using Supabase cache for ${usernameLower}`);
          
          // Ensure the URLs in profile_data are updated with any Supabase stored URLs
          let profileData = saved.profile_data as InstagramProfile;
          
          // Use the ImgBB URLs from Supabase if available
          if (saved.supabase_profile_pic_url) {
            profileData.profilePicUrl = saved.supabase_profile_pic_url;
          }
          
          if (saved.supabase_profile_hd_pic_url) {
            profileData.profilePicUrlHD = saved.supabase_profile_hd_pic_url;
          }
          
          // Transform ImgBB URLs to ensure they're valid
          profileData = transformImgBBUrls(profileData);
          
          const resp: FetchResponse = { 
            processed_result: profileData, 
            source: 'supabase_db_cache'
          };
          
          profileCache.set(usernameLower, { data: resp, timestamp: now });
          return resp;
        } else {
          console.log(`[DB Cache Expired] Cache for ${usernameLower} is ${Math.round(age)} minutes old`);
        }
      }
    }

    // 3) Edge Function 호출
    const toastId = `EDGE-${usernameLower}-${now}`;
    toast(toastId, 'Edge Function 호출 중...');

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), options.fetchTimeout || 180_000);

    try {
      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/instagram-profile`;
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.access_token || import.meta.env.VITE_SUPABASE_ANON_KEY}`,
      };

      console.log(`[API Call] Fetching profile data for ${usernameLower}`);
      const res = await fetchWithRetry(apiUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify({ instagramIDvalue: usernameLower }),
        signal: controller.signal,
        credentials: 'omit',
      }, 3, 300, options.onProgress);

      clearTimeout(timer);

      if (!res.ok) {
        const errPayload = await res.json().catch(() => ({}));
        // 404 + not_found => 계정 미존재
        if (res.status === 404 && errPayload.error === 'not_found') {
          throw new Error('not_found');
        }
        if (errPayload.error !== 'not_found') {
          console.error(`[API Error] ${res.status}:`, errPayload);
        }
        const errorMessage = errPayload.error || `Profile fetch failed (${res.status})`;
        const error = new Error(errorMessage);
        if (errPayload.errorCode) (error as any).code = errPayload.errorCode;
        throw error;
      }

      const responseData = await res.json();
      console.log(`[API Success] Received data for ${usernameLower}`);
      
      // Extract profile data from the response
      let profileData: FetchResponse;
      
      // Edge function now returns different format
      if (responseData.profile_data) {
        // New format with separate profile_data field
        profileData = {
          processed_result: responseData.profile_data,
          source: responseData.source || 'apify_api_with_imgbb'
        };
      } else if (responseData.processed_result) {
        // Original format with processed_result field
        profileData = responseData;
      } else {
        // Fallback to treating the entire response as the profile
        profileData = {
          processed_result: responseData,
          source: 'direct_api_response'
        };
      }
      
      // Transform and validate ImgBB URLs
      profileData.processed_result = transformImgBBUrls(profileData.processed_result);
      
      // Store in memory cache
      profileCache.set(usernameLower, { data: profileData, timestamp: now });
      
      return profileData;
    } catch (error: any) {
      if (error.message !== 'not_found') {
        console.error(`[Fetch Error] ${error.message}`);
      }
      
      // Record failure for cooldown
      failureCache.set(usernameLower, Date.now());
      
      // Format error for better user feedback
      if (error.name === 'AbortError') {
        throw new Error('요청 시간이 초과되었습니다. 잠시 후 다시 시도해주세요.');
      }
      
      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        throw new Error('네트워크 연결 오류. 인터넷 연결을 확인하고 다시 시도해주세요.');
      }
      
      // Pass through the original error
      throw error;
    } finally {
      clearTimeout(timer);
    }
  })();

  // 락 설정 및 관리
  ongoingRequests.set(usernameLower, promise);
  promise
    .catch((error) => {
      if (error.message !== 'not_found') {
        console.error(`[Request Error] ${usernameLower}:`, error);
      }
      failureCache.set(usernameLower, Date.now());
    })
    .finally(() => {
      ongoingRequests.delete(usernameLower);
      console.log(`[Request Done] Unlocked ${usernameLower}`);
    });

  return promise;
}